import { useState, useEffect, useCallback } from 'react';
import { getCVData, saveCVData, CVData } from '../utils/cvApi';
import { logger } from '../utils/logger';

interface UseCVSuggestionsResult {
  exhibitions: string[];
  publications: string[];
  awards: string[];
  isLoading: boolean;
  error: string | null;
  addExhibitionToCV: (name: string, type: 'solo' | 'group') => Promise<void>;
  addPublicationToCV: (title: string) => Promise<void>;
  addAwardToCV: (text: string) => Promise<void>;
}

/**
 * Type guard to filter out empty strings. Used for all CV text entries
 * (exhibitions, publications, awards) which are stored as `{ text }` objects.
 */
function isNonEmptyString(value: string | null | undefined): value is string {
  return Boolean(value && value.trim());
}

// Allow-list of fields accepted by POST /api/cv (mirrors backend CVCreate).
// `getCVData` returns CVResponse which includes server-managed identity fields
// (userId, updatedAt) and S3 asset-existence flags (hasProfileImage, hasCvPdf,
// hasLogo). The POST endpoint validates against CVCreate with extra="forbid"
// and 422s if any of those leak back in. Build the request payload from this
// allow-list so future additions to CVResponse cannot recur the bug — keep
// in sync with CVCreate's field set in backend/src/models/cv.py.
const CV_REQUEST_KEYS = [
  'biography',
  'socialMedia',
  'education',
  'soloExhibitions',
  'groupExhibitions',
  'publications',
  'awards',
  'contact',
  'collections',
  'galleryRepresentation',
  'additionalInfo',
  'entryPrivacy',
] as const satisfies ReadonlyArray<keyof CVData>;

function toCreatePayload(cv: CVData): Partial<CVData> {
  const out: Partial<CVData> = {};
  for (const key of CV_REQUEST_KEYS) {
    if (cv[key] !== undefined) {
      // Cast needed: indexed assignment with a `keyof CVData` from a union
      // of heterogeneous field types fails TS2322 without it.
      (out as Record<string, unknown>)[key] = cv[key];
    }
  }
  return out;
}

/**
 * Hook to provide CV-based autocomplete suggestions for exhibitions and publications.
 *
 * When ownerId is provided, fetches the catalog owner's CV data so that a
 * delegate sees the owner's exhibitions/publications as suggestions (fixes #952).
 */
export const useCVSuggestions = (ownerId?: string): UseCVSuggestionsResult => {
  const [exhibitions, setExhibitions] = useState<string[]>([]);
  const [publications, setPublications] = useState<string[]>([]);
  const [awards, setAwards] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCV = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const cvData = await getCVData(ownerId);

      // CV entries are stored as `{ text }` (CVTextEntry on the backend) — same
      // shape as awards. Reading `.exhibition` / `.publication` here was a
      // leftover from an older schema and silently produced empty suggestion
      // lists after the migration.
      const soloExhibitions = (cvData.soloExhibitions || [])
        .map(e => e.text)
        .filter(isNonEmptyString);

      const groupExhibitions = (cvData.groupExhibitions || [])
        .map(e => e.text)
        .filter(isNonEmptyString);

      const allExhibitions = [...new Set([...soloExhibitions, ...groupExhibitions])];
      setExhibitions(allExhibitions);

      const publicationTitles = (cvData.publications || [])
        .map(p => p.text)
        .filter(isNonEmptyString);
      setPublications(publicationTitles);

      // CVAward is `{ text }` only — no `year` or sub-type, unlike exhibitions/publications.
      // Do not "harmonize" this with the publications branch.
      const awardTexts = (cvData.awards || [])
        .map(a => a.text)
        .filter(text => text && text.trim());
      setAwards(awardTexts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load CV data');
      setExhibitions([]);
      setPublications([]);
      setAwards([]);
    } finally {
      setIsLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    fetchCV();
  }, [fetchCV]);

  // All three add*ToCV writers pass ownerId on BOTH the read-modify-write pair
  // and the refetch: when a delegate clicks "Add to CV" the entry must land on
  // the catalog OWNER's CV (the one the suggestions are fetched from), not the
  // delegate's personal CV. The backend gates the write via the delegation's
  // can_edit_cv permission (POST /api/cv?owner_id=...).
  const addExhibitionToCV = useCallback(async (name: string, type: 'solo' | 'group') => {
    try {
      const currentCV = await getCVData(ownerId);
      const newExhibition = { text: name };

      // CVTextEntry shape — single `text` field (matches awards + the manual
      // CV editor). Users format year/venue inline however they prefer.
      const key = type === 'solo' ? 'soloExhibitions' : 'groupExhibitions';
      const updated = [...(currentCV[key as 'soloExhibitions' | 'groupExhibitions'] || []), newExhibition];

      await saveCVData({
        ...toCreatePayload(currentCV),
        [key]: updated,
      }, ownerId);

      await fetchCV();
    } catch (err) {
      logger.error('Failed to add exhibition to CV', err);
      throw err;
    }
  }, [fetchCV, ownerId]);

  const addAwardToCV = useCallback(async (text: string) => {
    try {
      const currentCV = await getCVData(ownerId);

      // CVAward shape is `{ text }` only — do NOT prepend year or add other fields
      // (CVEditPage and the public CV renderer both expect this shape).
      const updatedAwards = [
        ...(currentCV.awards || []),
        { text },
      ];

      await saveCVData({
        ...toCreatePayload(currentCV),
        awards: updatedAwards,
      }, ownerId);

      await fetchCV();
    } catch (err) {
      logger.error('Failed to add award to CV', err);
      throw err;
    }
  }, [fetchCV, ownerId]);

  const addPublicationToCV = useCallback(async (title: string) => {
    try {
      const currentCV = await getCVData(ownerId);

      // CVTextEntry shape — see addExhibitionToCV note above.
      const newPublication = { text: title };
      const updatedPublications = [
        ...(currentCV.publications || []),
        newPublication,
      ];

      await saveCVData({
        ...toCreatePayload(currentCV),
        publications: updatedPublications,
      }, ownerId);

      await fetchCV();
    } catch (err) {
      logger.error('Failed to add publication to CV', err);
      throw err;
    }
  }, [fetchCV, ownerId]);

  return {
    exhibitions,
    publications,
    awards,
    isLoading,
    error,
    addExhibitionToCV,
    addPublicationToCV,
    addAwardToCV,
  };
};
